import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Plus, Ruler, Smartphone } from 'lucide-react';
import { PERMISSIONS, type CustomerMeasurementSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/utils/format';
import { apiErrorMessage } from '@/services/apiClient';
import { useCustomerMeasurementsQuery, useMeasurementSettingsQuery } from '../api/measurements.api';
import { DeviceCaptureDialog } from './DeviceCaptureDialog';
import { PhoneHandoverDialog } from './PhoneHandoverDialog';
import { RecordMeasurementDialog } from './RecordMeasurementDialog';

const QUALITY_TONES: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  HIGH: 'success',
  MEDIUM: 'warning',
  LOW: 'warning',
  REJECTED: 'danger',
};

/**
 * A customer's measurements, newest first.
 *
 * History rather than a current value: a re-measure adds a row, and every order keeps the figures
 * its lenses were made to. The badge on each row says how it was obtained, because a number from a
 * ruler and a number from a camera deserve different amounts of trust and the screen should not
 * flatten that.
 */
export function MeasurementHistory({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [isAdding, setAdding] = useState(false);
  const [isHandingOver, setHandingOver] = useState(false);
  const [isMeasuringHere, setMeasuringHere] = useState(false);

  const { data, isPending, isError, error } = useCustomerMeasurementsQuery(customerId);
  const canRecord = can(PERMISSIONS.MEASUREMENTS_CREATE);
  const settings = useMeasurementSettingsQuery();
  // Camera flows exist only where the platform has switched them on.
  const cameraAvailable =
    settings.data !== undefined && settings.data.cameraMeasurementMode !== 'OFF';

  return (
    <Card>
      <CardHeader
        title={t('measurements.title')}
        subtitle={t('measurements.subtitle')}
        action={
          canRecord ? (
            <div className="flex flex-wrap gap-2">
              {cameraAvailable ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setMeasuringHere(true)}>
                    <Camera className="size-4" aria-hidden />
                    {t('measurements.device.open')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setHandingOver(true)}>
                    <Smartphone className="size-4" aria-hidden />
                    {t('measurements.phone.open')}
                  </Button>
                </>
              ) : null}
              <Button size="sm" onClick={() => setAdding(true)}>
                <Plus className="size-4" aria-hidden />
                {t('measurements.add')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {isError ? (
        <div className="p-4 pt-0">
          <Alert tone="error">{apiErrorMessage(error) ?? t('measurements.errors.load')}</Alert>
        </div>
      ) : isPending ? (
        <LoadingState />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          icon={Ruler}
          title={t('measurements.empty')}
          description={t('measurements.emptyHint')}
        />
      ) : (
        <ul className="divide-line divide-y">
          {(data ?? []).map((measurement) => (
            <MeasurementRow key={measurement.id} measurement={measurement} />
          ))}
        </ul>
      )}

      <RecordMeasurementDialog
        customerId={customerId}
        isOpen={isAdding}
        onClose={() => setAdding(false)}
      />
      {cameraAvailable ? (
        <>
          <DeviceCaptureDialog
            customerId={customerId}
            isOpen={isMeasuringHere}
            onClose={() => setMeasuringHere(false)}
          />
          <PhoneHandoverDialog
            customerId={customerId}
            isOpen={isHandingOver}
            onClose={() => setHandingOver(false)}
          />
        </>
      ) : null}
    </Card>
  );
}

function MeasurementRow({ measurement }: { measurement: CustomerMeasurementSummary }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const pd = [
    measurement.rightPdMm ? `R ${measurement.rightPdMm}` : null,
    measurement.leftPdMm ? `L ${measurement.leftPdMm}` : null,
    measurement.totalPdMm ? `${t('measurements.total')} ${measurement.totalPdMm}` : null,
  ].filter(Boolean);

  return (
    <li className="flex flex-wrap items-center gap-3 p-4">
      <span className="min-w-0 flex-1">
        <span className="text-ink block font-medium" dir="ltr">
          {pd.length > 0 ? pd.join(' · ') : t('measurements.faceOnly')}
        </span>
        <span className="text-muted text-xs">
          {measurement.pdType ? `${t(`measurements.pdTypeTag.${measurement.pdType}`)} · ` : ''}
          {formatDate(measurement.measuredAt, locale)}
          {measurement.measuredByName ? ` · ${measurement.measuredByName}` : ''}
        </span>
      </span>

      <Badge tone={measurement.source === 'MANUAL' ? 'neutral' : 'brand'}>
        {t(`measurements.sources.${measurement.source}`)}
      </Badge>

      {/* Absent for a manual entry on purpose — a ruler is not an estimate. */}
      {measurement.quality ? (
        <Badge tone={QUALITY_TONES[measurement.quality] ?? 'neutral'}>
          {t(`measurements.quality.${measurement.quality}`)}
        </Badge>
      ) : null}

      {/* Benchmark data is kept visible, and plainly labelled, so nobody mistakes it for a clinical figure. */}
      {measurement.isValidation ? (
        <Badge tone="warning">{t('measurements.validation')}</Badge>
      ) : null}

      {/* A camera estimate is history, and readable as one: the picker refuses it and so does the API. */}
      {measurement.referenceOnly ? (
        <Badge tone="warning">{t('measurements.camera.badge')}</Badge>
      ) : null}

      {measurement.isSuperseded ? (
        <span className="text-muted text-xs">{t('measurements.superseded')}</span>
      ) : null}
    </li>
  );
}
